// Web Audio synth engine for playing MidiMate results in the browser. Replaces the General
// MIDI soundfont with electronic-music sounds: supersaws, filtered saw bass, 808 sub, FM
// Rhodes, plucks, mallets, 909/808-style drums, sidechain pumping, reverb and delay.
// Everything is scheduled on the AudioContext clock; see player.js for the transport.

import { nearestSample } from './sampler';

const midiHz = (m) => 440 * 2 ** ((m - 69) / 12);

// Per-patch mix settings for sampled sounds: bus, level, release seconds, reverb send
const SAMPLE_MIX = {
  synth_lead:   { bus: 'dry',  gain: 0.4,  release: 0.15, reverb: 0.25, big: true, delay: 0.2 },
  pad:          { bus: 'pump', gain: 0.4,  release: 0.7,  reverb: 0.45, big: true, attack: 0.25 },
  strings:      { bus: 'pump', gain: 0.4,  release: 0.5,  reverb: 0.4,  big: true, attack: 0.12 },
  stab:         { bus: 'pump', gain: 0.5,  release: 0.1,  reverb: 0.15 },
  brass:        { bus: 'dry',  gain: 0.45, release: 0.12, reverb: 0.2 },
  ep:           { bus: 'pump', gain: 0.55, release: 0.3,  reverb: 0.2 },
  piano:        { bus: 'pump', gain: 0.55, release: 0.25, reverb: 0.25, big: true },
  organ:        { bus: 'pump', gain: 0.4,  release: 0.06, reverb: 0.2 },
  mallet:       { bus: 'dry',  gain: 0.5,  release: 0.4,  reverb: 0.3,  big: true, delay: 0.2 },
  bell:         { bus: 'dry',  gain: 0.45, release: 0.5,  reverb: 0.4,  big: true, delay: 0.2 },
  pluck:        { bus: 'dry',  gain: 0.45, release: 0.12, reverb: 0.25, delay: 0.15 },
  soft_lead:    { bus: 'dry',  gain: 0.45, release: 0.18, reverb: 0.3,  big: true, delay: 0.2 },
  rolling_bass: { bus: 'pump', gain: 0.7,  release: 0.04 },
  reese:        { bus: 'pump', gain: 0.6,  release: 0.06 },
  warm_bass:    { bus: 'pump', gain: 0.7,  release: 0.08 },
  sub808:       { bus: 'pump', gain: 0.8,  release: 0.08, minHold: 0.4 },
};
const DRUM_MIX = {
  36: [0.9, 0], 35: [0.9, 0], 38: [0.7, 0.05], 40: [0.7, 0.05], 39: [0.6, -0.05], 37: [0.5, -0.2],
  42: [0.32, 0.25], 44: [0.28, 0.25], 46: [0.36, 0.25], 49: [0.35, 0.3], 51: [0.3, 0.35], 54: [0.3, 0.4],
  56: [0.35, -0.3], 62: [0.5, -0.15], 63: [0.5, -0.05], 64: [0.5, 0.05], 69: [0.3, 0.45], 70: [0.3, 0.45], 75: [0.4, -0.4],
};

export function createEngine(ctx, style = 'house') {
  const now = () => ctx.currentTime;
  const sources = new Set();     // sources still sounding, so stopAll can cut them
  let pack = null;               // decoded sample pack (see sampler.js), null = oscillators only

  // ---------------------------------------------------------------- master chain
  const master = ctx.createGain();
  master.gain.value = 0.9;
  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -14;
  comp.knee.value = 12;
  comp.ratio.value = 4;
  comp.attack.value = 0.004;
  comp.release.value = 0.16;
  const clip = ctx.createWaveShaper();
  clip.curve = driveCurve(1.4);
  clip.oversample = '2x';
  const out = ctx.createGain();
  out.gain.value = 0.85;
  let masterIn = master;
  if (style === 'lofi') {
    const lofiLp = ctx.createBiquadFilter();
    lofiLp.type = 'lowpass';
    lofiLp.frequency.value = 7500;
    master.connect(lofiLp);
    lofiLp.connect(comp);
  } else {
    master.connect(comp);
  }
  comp.connect(clip);
  clip.connect(out);
  out.connect(ctx.destination);

  const dry = ctx.createGain();              // drums, leads: no sidechain
  dry.connect(masterIn);
  const pump = ctx.createGain();             // bass, chords, pads: ducked by the kick
  pump.connect(masterIn);
  const pumpOn = style === 'house' || style === 'futurebass';
  const pumpDepth = style === 'futurebass' ? 0.7 : 0.4;

  // ---------------------------------------------------------------- shared buffers / fx
  const noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  {
    const d = noiseBuf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }
  const noise = (t0, tEnd) => {
    const s = ctx.createBufferSource();
    s.buffer = noiseBuf;
    s.loop = true;
    s.loopStart = Math.random() * 1.5;
    s.loopEnd = s.loopStart + 0.5;
    start(s, t0, tEnd);
    return s;
  };

  const reverbSmall = convolver(ctx, 0.9, 0.012, 0.55);
  const reverbBig = convolver(ctx, 2.4, 0.03, 0.4);
  reverbSmall.connect(masterIn);
  reverbBig.connect(masterIn);

  const echo = ctx.createDelay(2);
  const echoFb = ctx.createGain();
  const echoLp = ctx.createBiquadFilter();
  echoLp.type = 'lowpass';
  echoLp.frequency.value = 3500;
  echo.connect(echoLp);
  echoLp.connect(echoFb);
  echoFb.connect(echo);
  const echoOut = ctx.createGain();
  echoLp.connect(echoOut);
  echoOut.connect(masterIn);
  const setTempo = (bpm) => {
    echo.delayTime.value = (60 / bpm) * 0.75;   // dotted eighth
    echoFb.gain.value = 0.38;
  };
  setTempo(120);

  if (style === 'lofi') {
    // vinyl bed: crackle + hiss, quiet, for the whole session
    const bed = ctx.createBuffer(2, ctx.sampleRate * 4, ctx.sampleRate);
    for (let c = 0; c < 2; c++) {
      const d = bed.getChannelData(c);
      for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * 0.004;
      for (let k = 0; k < 56; k++) {
        const i = Math.floor(Math.random() * (d.length - 40));
        const a = (0.2 + Math.random() * 0.8) * (Math.random() < 0.5 ? -1 : 1) * 0.06;
        for (let j = 0; j < 30; j++) d[i + j] += a * Math.exp(-j / 6) * Math.cos(j * 0.9);
      }
    }
    const s = ctx.createBufferSource();
    s.buffer = bed;
    s.loop = true;
    const g = ctx.createGain();
    g.gain.value = 1;
    s.connect(g);
    g.connect(masterIn);
    s.start();
    sources.add(s);
  }

  // ---------------------------------------------------------------- helpers
  function start(src, t0, tEnd) {
    src.start(t0);
    src.stop(tEnd);
    sources.add(src);
    src.onended = () => sources.delete(src);
  }

  function osc(type, freq, t0, tEnd, detune = 0) {
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    o.detune.value = detune;
    start(o, t0, tEnd);
    return o;
  }

  // ADSR on a gain param; returns the time the voice is silent
  function adsr(param, t0, a, d, s, r, gateEnd, peak) {
    const aEnd = t0 + Math.max(a, 0.003);
    param.setValueAtTime(0, t0);
    param.linearRampToValueAtTime(peak, aEnd);
    param.setTargetAtTime(peak * s, aEnd, Math.max(d, 0.01) / 3);
    const rel = Math.max(gateEnd, aEnd + 0.005);
    param.setTargetAtTime(0, rel, Math.max(r, 0.01) / 4);
    return rel + r * 1.5 + 0.05;
  }

  function decayEnv(param, t0, peak, tau, t1 = t0) {
    param.setValueAtTime(peak, t0);
    param.setTargetAtTime(0, t1, tau);
    return t1 + tau * 6;
  }

  function lowpass(fc, q = 0) {
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = fc;
    f.Q.value = q;                  // dB for lowpass in Web Audio
    return f;
  }

  function filterEnv(f, t0, peakHz, baseHz, tau) {
    f.frequency.setValueAtTime(Math.min(peakHz, 18000), t0);
    f.frequency.setTargetAtTime(baseHz, t0, tau);
  }

  function pannerAt(p) {
    const pn = ctx.createStereoPanner();
    pn.pan.value = p;
    return pn;
  }

  function shaper(amount) {
    const w = ctx.createWaveShaper();
    w.curve = driveCurve(amount);
    return w;
  }

  // Connect a voice's last node to the bus, plus sends. `mods` are the character words
  // from the track name (bright/dark, wide/narrow, wet/dry, soft/hard).
  let mods = null;
  function route(node, bus, { reverb = 0, big = false, delay = 0 } = {}) {
    if (mods) {
      if (mods.bright || mods.dark) {
        const sh = ctx.createBiquadFilter();
        sh.type = 'highshelf';
        sh.frequency.value = 2000;
        sh.gain.value = mods.bright ? 5 : -7;
        node.connect(sh);
        node = sh;
      }
      if (mods.narrow) {
        const mono = ctx.createGain();
        mono.channelCount = 1;
        mono.channelCountMode = 'explicit';
        node.connect(mono);
        node = mono;
      }
      if (mods.wet) reverb = Math.min(reverb * 1.8 + 0.15, 0.9);
      if (mods.dry) reverb = 0;
      if (mods.soft) {
        const g = ctx.createGain();
        g.gain.value = 0.7;
        node.connect(g);
        node = g;
      }
    }
    node.connect(bus);
    if (reverb > 0) {
      const g = ctx.createGain();
      g.gain.value = reverb;
      node.connect(g);
      g.connect(big ? reverbBig : reverbSmall);
    }
    if (delay > 0) {
      const g = ctx.createGain();
      g.gain.value = delay;
      node.connect(g);
      g.connect(echo);
    }
  }

  function supersawOsc(f, t0, tEnd, voices, spreadCents, stereo = true, perVoicePan = false) {
    // Detuned saws fanned left/right: one panner per voice (as render.py does) or two
    // shared ones, which is cheaper
    if (mods && mods.wide) spreadCents *= 1.6;
    const sum = ctx.createGain();
    const left = stereo && !perVoicePan ? pannerAt(-0.7) : sum;
    const right = stereo && !perVoicePan ? pannerAt(0.7) : sum;
    if (stereo && !perVoicePan) { left.connect(sum); right.connect(sum); }
    for (let i = 0; i < voices; i++) {
      const o = voices === 1 ? 0 : (i / (voices - 1)) * 2 - 1;
      const v = osc('sawtooth', f, t0, tEnd, o * spreadCents);
      const g = ctx.createGain();
      g.gain.value = 1 / Math.sqrt(voices);
      v.connect(g);
      if (perVoicePan) {
        const p = pannerAt(o * 0.9);
        g.connect(p);
        p.connect(sum);
      } else {
        g.connect(o < -0.01 ? left : o > 0.01 ? right : sum);
      }
    }
    return sum;
  }

  // ---------------------------------------------------------------- pitched patches
  // Each takes (f, vel 0..1, t0, gate seconds) and routes itself.
  const patches = {
    supersaw(f, vel, t0, gate) {
      // The render.py supersaw Matteen picked for the future bass reel: seven saws fanned
      // across the stereo field, a filter that opens on each chord and settles over 300 ms,
      // big reverb, and (in future bass) a hard sidechain pump
      const amp = ctx.createGain();
      const tEnd = adsr(amp.gain, t0, 0.02, 0.3, 0.9, 0.25, t0 + gate, 0.16 + 0.2 * vel);
      const src = supersawOsc(f, t0, tEnd, 7, 22.4, true, true);
      const flt = lowpass(4000, 0);
      filterEnv(flt, t0, 5500 + 4500 * vel, 2500 + 4500 * vel, 0.3);
      src.connect(flt);
      flt.connect(amp);
      route(amp, pump, { reverb: 0.45, big: true });
    },
    synth_lead(f, vel, t0, gate) {
      const amp = ctx.createGain();
      const tEnd = adsr(amp.gain, t0, 0.01, 0.2, 0.8, 0.15, t0 + gate, 0.13 + 0.14 * vel);
      const src = supersawOsc(f, t0, tEnd, 5, 12);
      const flt = lowpass(5000, 0);
      src.connect(flt);
      flt.connect(amp);
      route(amp, dry, { reverb: 0.3, big: true, delay: 0.3 });
    },
    pad(f, vel, t0, gate) {
      const amp = ctx.createGain();
      const tEnd = adsr(amp.gain, t0, 0.45, 0.5, 0.9, 0.8, t0 + Math.max(gate, 0.4), 0.08 + 0.1 * vel);
      const src = supersawOsc(f, t0, tEnd, 5, 16);
      const flt = lowpass(900 + 900 * vel, 0);
      const lfo = osc('sine', 0.25, t0, tEnd);
      const lfoG = ctx.createGain();
      lfoG.gain.value = 450;
      lfo.connect(lfoG);
      lfoG.connect(flt.frequency);
      src.connect(flt);
      flt.connect(amp);
      route(amp, pump, { reverb: 0.6, big: true });
    },
    strings(f, vel, t0, gate) {
      const amp = ctx.createGain();
      const tEnd = adsr(amp.gain, t0, 0.25, 0.4, 0.9, 0.5, t0 + Math.max(gate, 0.3), 0.09 + 0.1 * vel);
      const src = supersawOsc(f, t0, tEnd, 3, 10);
      const flt = lowpass(1800 + 1800 * vel, 0);
      src.connect(flt);
      flt.connect(amp);
      route(amp, pump, { reverb: 0.55, big: true });
    },
    stab(f, vel, t0, gate) {
      // Filtered house/garage chord stab: short, dark, a little drive. Keep the sweep small.
      const amp = ctx.createGain();
      const tEnd = adsr(amp.gain, t0, 0.005, 0.2, 0.35, 0.1, t0 + Math.min(gate, 0.6), 0.14 + 0.18 * vel);
      const src = supersawOsc(f, t0, tEnd, 3, 7);
      const low = osc('sawtooth', f / 2, t0, tEnd);
      const lowG = ctx.createGain();
      lowG.gain.value = 0.3;
      low.connect(lowG);
      const flt = lowpass(500, 1);
      filterEnv(flt, t0, 1100 + 1500 * vel, 500 + 300 * vel, 0.14);
      const drv = shaper(1.5);
      src.connect(flt);
      lowG.connect(flt);
      flt.connect(drv);
      drv.connect(amp);
      route(amp, pump, { reverb: 0.2 });
    },
    brass(f, vel, t0, gate) {
      const amp = ctx.createGain();
      const tEnd = adsr(amp.gain, t0, 0.06, 0.25, 0.7, 0.12, t0 + gate, 0.12 + 0.16 * vel);
      const src = supersawOsc(f, t0, tEnd, 3, 8, false);
      const flt = lowpass(800, 2);
      filterEnv(flt, t0, 1200 + 2500 * vel, 900 + 1500 * vel, 0.25);
      src.connect(flt);
      flt.connect(amp);
      route(amp, dry, { reverb: 0.3 });
    },
    ep(f, vel, t0, gate) {
      // two-operator FM electric piano with a tine partial and Rhodes-style pan tremolo
      const tau = 1.6 * (60 / Math.max(f, 60)) ** 0.3;
      const amp = ctx.createGain();
      const tEnd = adsr(amp.gain, t0, 0.002, tau, 0, 0.3, t0 + gate, 0.22 + 0.3 * vel);
      const car = osc('sine', f, t0, tEnd);
      const mod = osc('sine', f, t0, tEnd);
      const modG = ctx.createGain();
      decayEnv(modG.gain, t0, (0.6 + 1.6 * vel) * f, 0.35);
      mod.connect(modG);
      modG.connect(car.frequency);
      const tine = osc('sine', f * 7, t0, t0 + 0.4);
      const tineG = ctx.createGain();
      decayEnv(tineG.gain, t0, 0.12 * vel, 0.06);
      tine.connect(tineG);
      const pn = pannerAt(0);
      const lfo = osc('sine', 4.6, t0, tEnd);
      const lfoG = ctx.createGain();
      lfoG.gain.value = 0.35;
      lfo.connect(lfoG);
      lfoG.connect(pn.pan);
      car.connect(pn);
      tineG.connect(pn);
      pn.connect(amp);
      const flt = style === 'lofi' ? lowpass(3200) : null;
      if (flt) {
        amp.connect(flt);
        route(flt, pump, { reverb: 0.2 });
      } else {
        route(amp, pump, { reverb: 0.25 });
      }
    },
    piano(f, vel, t0, gate) {
      // additive: three slightly inharmonic partials with different decays, plus a hammer
      const tau = 2.2 * (110 / Math.max(f, 110)) ** 0.4;
      const amp = ctx.createGain();
      const tEnd = adsr(amp.gain, t0, 0.002, tau, 0, 0.25, t0 + gate, 0.2 + 0.3 * vel);
      const sum = ctx.createGain();
      [[1, 1, 1], [2.003, 0.5, 0.5], [3.01, 0.25, 0.3], [4.02, 0.1, 0.2]].forEach(([r, a, dec]) => {
        const o = osc('sine', f * r, t0, tEnd);
        const g = ctx.createGain();
        decayEnv(g.gain, t0, a, tau * dec);
        o.connect(g);
        g.connect(sum);
      });
      const hammer = noise(t0, t0 + 0.05);
      const hg = ctx.createGain();
      decayEnv(hg.gain, t0, 0.25 * vel, 0.004);
      const hf = lowpass(3000);
      hammer.connect(hf);
      hf.connect(hg);
      hg.connect(sum);
      const flt = lowpass(2500 + 5000 * vel, 0);
      sum.connect(flt);
      flt.connect(amp);
      route(amp, pump, { reverb: 0.3, big: true });
    },
    organ(f, vel, t0, gate) {
      const amp = ctx.createGain();
      const tEnd = adsr(amp.gain, t0, 0.01, 0.05, 1, 0.05, t0 + gate, 0.1 + 0.1 * vel);
      const sum = ctx.createGain();
      [[1, 1], [2, 0.7], [3, 0.4], [4, 0.3], [0.5, 0.5]].forEach(([r, a]) => {
        const o = osc('sine', f * r, t0, tEnd);
        const g = ctx.createGain();
        g.gain.value = a;
        o.connect(g);
        g.connect(sum);
      });
      const lfo = osc('sine', 6, t0, tEnd);
      const lfoG = ctx.createGain();
      lfoG.gain.value = 0.08;
      lfo.connect(lfoG);
      lfoG.connect(amp.gain);
      sum.connect(amp);
      route(amp, pump, { reverb: 0.25 });
    },
    mallet(f, vel, t0) {
      const sum = ctx.createGain();
      [[1, 1, 0.35], [4, 0.45, 0.07], [9.93, 0.15, 0.03]].forEach(([r, a, tau]) => {
        const o = osc('sine', f * r, t0, t0 + tau * 6);
        const g = ctx.createGain();
        decayEnv(g.gain, t0, a, tau);
        o.connect(g);
        g.connect(sum);
      });
      const click = noise(t0, t0 + 0.03);
      const cf = ctx.createBiquadFilter();
      cf.type = 'highpass';
      cf.frequency.value = 2500;
      const cg = ctx.createGain();
      decayEnv(cg.gain, t0, 0.4, 0.003);
      click.connect(cf);
      cf.connect(cg);
      cg.connect(sum);
      const amp = ctx.createGain();
      amp.gain.value = 0.2 + 0.3 * vel;
      sum.connect(amp);
      const pn = pannerAt(0.15);
      amp.connect(pn);
      route(pn, dry, { reverb: 0.35, big: true, delay: 0.3 });
    },
    bell(f, vel, t0) {
      const sum = ctx.createGain();
      [[1, 1, 0.9], [2.76, 0.5, 0.5], [5.4, 0.25, 0.3], [8.93, 0.1, 0.15]].forEach(([r, a, tau]) => {
        const o = osc('sine', f * r, t0, t0 + tau * 6);
        const g = ctx.createGain();
        decayEnv(g.gain, t0, a, tau);
        o.connect(g);
        g.connect(sum);
      });
      const amp = ctx.createGain();
      amp.gain.value = 0.12 + 0.18 * vel;
      sum.connect(amp);
      route(amp, dry, { reverb: 0.5, big: true, delay: 0.25 });
    },
    pluck(f, vel, t0, gate) {
      // Soft, dark pluck (think melodic techno), not a chiptune blip: saw + triangle, gentle
      // filter envelope, little resonance
      const amp = ctx.createGain();
      const tEnd = adsr(amp.gain, t0, 0.003, 0.3, 0.2, 0.12, t0 + Math.min(gate, 0.5), 0.14 + 0.16 * vel);
      const sw = osc('sawtooth', f, t0, tEnd);
      const tr = osc('triangle', f, t0, tEnd);
      const trG = ctx.createGain();
      trG.gain.value = 0.6;
      tr.connect(trG);
      const flt = lowpass(500, 1);
      filterEnv(flt, t0, 1400 + 1800 * vel, 450, 0.13);
      sw.connect(flt);
      trG.connect(flt);
      flt.connect(amp);
      const pn = pannerAt(0.1);
      amp.connect(pn);
      route(pn, dry, { reverb: 0.3, big: true, delay: 0.14 });
    },
    soft_lead(f, vel, t0, gate) {
      const amp = ctx.createGain();
      const tEnd = adsr(amp.gain, t0, 0.03, 0.3, 0.8, 0.18, t0 + gate, 0.18 + 0.2 * vel);
      const t = osc('triangle', f, t0, tEnd);
      const s = osc('sine', f * 2, t0, tEnd);
      const sg = ctx.createGain();
      sg.gain.value = 0.35;
      s.connect(sg);
      const vib = osc('sine', 5.5, t0, tEnd);
      const vibG = ctx.createGain();
      vibG.gain.setValueAtTime(0, t0);
      vibG.gain.linearRampToValueAtTime(f * 0.004, t0 + 0.25);
      vib.connect(vibG);
      vibG.connect(t.frequency);
      vibG.connect(s.frequency);
      const flt = lowpass(1800 + 1200 * vel, 0);
      t.connect(flt);
      sg.connect(flt);
      flt.connect(amp);
      const pn = pannerAt(-0.1);
      amp.connect(pn);
      route(pn, dry, { reverb: 0.35, big: true, delay: 0.3 });
    },
    rolling_bass(f, vel, t0, gate) {
      const amp = ctx.createGain();
      const tEnd = adsr(amp.gain, t0, 0.002, 0.12, 0.7, 0.03, t0 + Math.min(gate, 0.4), 0.3 + 0.25 * vel);
      const s = osc('sawtooth', f, t0, tEnd);
      const sub = osc('sine', f / 2, t0, tEnd);
      const subG = ctx.createGain();
      subG.gain.value = 0.7;
      sub.connect(subG);
      const flt = lowpass(110, 6);
      filterEnv(flt, t0, 800 + 1100 * vel, 110, 0.09);
      const drv = shaper(1.8);
      s.connect(flt);
      subG.connect(flt);
      flt.connect(drv);
      drv.connect(amp);
      route(amp, pump);
    },
    reese(f, vel, t0, gate) {
      const amp = ctx.createGain();
      const tEnd = adsr(amp.gain, t0, 0.01, 0.2, 0.8, 0.06, t0 + gate, 0.25 + 0.2 * vel);
      const src = supersawOsc(f, t0, tEnd, 3, 14, false);
      const flt = lowpass(300 + 500 * vel, 4);
      const drv = shaper(2.2);
      src.connect(flt);
      flt.connect(drv);
      drv.connect(amp);
      route(amp, pump);
    },
    warm_bass(f, vel, t0, gate) {
      const amp = ctx.createGain();
      const tEnd = adsr(amp.gain, t0, 0.006, 0.3, 0.8, 0.08, t0 + gate, 0.35 + 0.2 * vel);
      const s = osc('sine', f, t0, tEnd);
      const t = osc('triangle', f, t0, tEnd);
      const tg = ctx.createGain();
      tg.gain.value = 0.35;
      t.connect(tg);
      const flt = lowpass(450, 0);
      s.connect(flt);
      tg.connect(flt);
      flt.connect(amp);
      route(amp, pump);
    },
    sub808(f, vel, t0, gate) {
      const amp = ctx.createGain();
      const hold = Math.max(gate, 0.4);
      const tEnd = adsr(amp.gain, t0, 0.002, 0.5, 0.35, 0.06, t0 + hold, 0.4 + 0.25 * vel);
      const s = osc('sine', f * 2.5, t0, tEnd);
      s.frequency.setValueAtTime(f * 2.5, t0);
      s.frequency.exponentialRampToValueAtTime(f, t0 + 0.03);
      const drv = shaper(2.5);
      s.connect(drv);
      drv.connect(amp);
      route(amp, pump);
    },
  };

  // ---------------------------------------------------------------- sampled voices
  function sampleNote(name, midi, vel, t0, gate) {
    const p = pack && pack.patches[name];
    const mix = SAMPLE_MIX[name];
    if (!p || !p.samples.length || !mix) return false;   // no mix entry = keep the oscillator patch
    const smp = nearestSample(p.samples, midi);
    const src = ctx.createBufferSource();
    src.buffer = smp.buffer;
    src.playbackRate.value = 2 ** ((midi - smp.note) / 12);
    if (p.loop) {
      src.loop = true;
      src.loopStart = p.loop[0] + pack.offset;
      src.loopEnd = p.loop[1] + pack.offset;
    }
    const amp = ctx.createGain();
    const hold = Math.max(gate, mix.minHold || 0);
    const attack = mix.attack || 0.004;
    const peak = mix.gain * (0.35 + 0.65 * vel);
    amp.gain.setValueAtTime(0, t0);
    amp.gain.linearRampToValueAtTime(peak, t0 + attack);
    let tEnd;
    if (p.kind === 'oneshot') {
      // Let decaying sounds ring past the key, within reason
      const natural = (smp.buffer.duration - pack.offset) / src.playbackRate.value;
      const rel = Math.min(t0 + hold + 1.2, t0 + natural);
      amp.gain.setTargetAtTime(0, rel, mix.release / 3);
      tEnd = rel + mix.release * 2 + 0.05;
    } else {
      amp.gain.setTargetAtTime(0, t0 + hold, mix.release / 4);
      tEnd = t0 + hold + mix.release * 1.5 + 0.05;
    }
    let input = amp;
    if (mix.hp) {
      // keep chords out of the bass's way: two highpass stages (24 dB/oct)
      const f1 = ctx.createBiquadFilter();
      const f2 = ctx.createBiquadFilter();
      f1.type = f2.type = 'highpass';
      f1.frequency.value = f2.frequency.value = mix.hp;
      f1.connect(f2);
      if (mix.shelf) {
        const sh = ctx.createBiquadFilter();
        sh.type = 'highshelf';
        sh.frequency.value = 2500;
        sh.gain.value = mix.shelf;
        f2.connect(sh);
        sh.connect(amp);
      } else {
        f2.connect(amp);
      }
      input = f1;
    }
    src.connect(input);
    src.start(t0, pack.offset);
    src.stop(tEnd);
    sources.add(src);
    src.onended = () => sources.delete(src);
    if (mix.octave) {
      // octave-up layer for lift
      const up = ctx.createBufferSource();
      up.buffer = smp.buffer;
      up.playbackRate.value = src.playbackRate.value * 2;
      up.loop = src.loop;
      up.loopStart = src.loopStart;
      up.loopEnd = src.loopEnd;
      const g = ctx.createGain();
      g.gain.value = mix.octave;
      up.connect(g);
      g.connect(input);
      up.start(t0, pack.offset);
      up.stop(tEnd);
      sources.add(up);
      up.onended = () => sources.delete(up);
    }
    route(amp, mix.bus === 'pump' ? pump : dry, { reverb: mix.reverb || 0, big: !!mix.big, delay: mix.delay || 0 });
    return true;
  }

  function sampleDrum(note, vel, t0) {
    const buffer = pack && pack.kit[note === 35 ? 36 : note === 40 ? 38 : note];
    if (!buffer) return false;
    const [vol, p] = DRUM_MIX[note] || [0.4, 0];
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const g = ctx.createGain();
    g.gain.value = vol * (0.4 + 0.6 * vel);
    const pn = pannerAt(p);
    src.connect(g);
    g.connect(pn);
    route(pn, drumBus, { reverb: note === 38 || note === 40 || note === 39 ? 0.12 : 0 });
    src.start(t0, pack.offset);
    src.stop(t0 + buffer.duration + 0.05);
    sources.add(src);
    src.onended = () => sources.delete(src);
    if ((note === 35 || note === 36) && pumpOn) sidechain(t0);
    return true;
  }

  // ---------------------------------------------------------------- drums
  const kit = style === 'trap' ? 'trap' : style === 'lofi' ? 'lofi' : style === 'acoustic' || style === 'pop' ? 'acoustic' : 'house';
  let drumBus = ctx.createGain();
  drumBus.gain.value = 1;
  if (kit === 'lofi') {
    const lp = lowpass(6500);
    drumBus.connect(lp);
    lp.connect(dry);
  } else {
    drumBus.connect(dry);
  }

  function hit(node, vol, p = 0, reverb = 0) {
    const g = ctx.createGain();
    g.gain.value = vol;
    const pn = pannerAt(p);
    node.connect(g);
    g.connect(pn);
    route(pn, drumBus, { reverb });
  }

  function noiseHit(t0, filterType, fc, q, tau, vol, p, reverb = 0, env = null) {
    const n = noise(t0, t0 + tau * 7 + 0.05);
    const f = ctx.createBiquadFilter();
    f.type = filterType;
    f.frequency.value = fc;
    f.Q.value = q;
    const g = ctx.createGain();
    if (env) env(g.gain); else decayEnv(g.gain, t0, 1, tau);
    n.connect(f);
    f.connect(g);
    hit(g, vol, p, reverb);
  }

  function toneHit(t0, f0, f1, glide, tau, vol, p, reverb = 0, type = 'sine') {
    const o = osc(type, f0, t0, t0 + tau * 7 + 0.05);
    if (f1 !== f0) {
      o.frequency.setValueAtTime(f0, t0);
      o.frequency.exponentialRampToValueAtTime(f1, t0 + glide);
    }
    const g = ctx.createGain();
    decayEnv(g.gain, t0, 1, tau);
    o.connect(g);
    hit(g, vol, p, reverb);
  }

  function drum(note, vel, t0) {
    const v = 0.4 + 0.6 * vel;
    if (note === 35 || note === 36) {
      const [f0, f1, tau] = kit === 'trap' ? [110, 44, 0.42] : kit === 'lofi' ? [150, 50, 0.13]
        : kit === 'acoustic' ? [160, 55, 0.16] : [190, 47, 0.19];
      const o = osc('sine', f0, t0, t0 + tau * 7);
      o.frequency.setValueAtTime(f0, t0);
      o.frequency.exponentialRampToValueAtTime(f1, t0 + 0.045);
      const g = ctx.createGain();
      decayEnv(g.gain, t0, 1, tau);
      const drv = shaper(1.6);
      o.connect(g);
      g.connect(drv);
      hit(drv, 0.9 * v);
      if (kit !== 'lofi') noiseHit(t0, 'highpass', 1500, 0, 0.0025, 0.45 * v, 0);
      if (pumpOn) sidechain(t0);
      return;
    }
    if (note === 38 || note === 40) {
      toneHit(t0, kit === 'acoustic' ? 200 : 185, kit === 'acoustic' ? 170 : 185, 0.02, 0.06, 0.45 * v, 0.05);
      noiseHit(t0, 'bandpass', kit === 'lofi' ? 2200 : 3200, 0.45, kit === 'trap' ? 0.12 : 0.16, 0.7 * v, 0.05, 0.15);
      return;
    }
    if (note === 39) {
      noiseHit(t0, 'bandpass', 2400, 0.6, 0.12, 0.65 * v, -0.05, 0.2, (gain) => {
        for (let k = 0; k < 3; k++) {
          gain.setValueAtTime(1, t0 + k * 0.011);
          gain.setTargetAtTime(0, t0 + k * 0.011, 0.008);
        }
        gain.setValueAtTime(0.9, t0 + 0.03);
        gain.setTargetAtTime(0, t0 + 0.03, 0.12);
      });
      return;
    }
    if (note === 42 || note === 44 || note === 46) {
      const tau = { 42: 0.035, 44: 0.02, 46: 0.28 }[note];
      const vol = (note === 46 ? 0.3 : 0.2) * v;
      // highpassed noise for the air plus a resonant band for a bit of metal
      noiseHit(t0, 'highpass', kit === 'lofi' ? 6000 : 8500, 0, tau, vol, 0.25);
      noiseHit(t0, 'bandpass', 10500, 6, tau * 0.8, vol * 0.8, 0.25);
      return;
    }
    if (note === 37) {
      toneHit(t0, 1200, 1200, 0, 0.008, 0.5 * v, -0.2);
      noiseHit(t0, 'highpass', 3000, 0, 0.004, 0.4 * v, -0.2);
      return;
    }
    if ([41, 43, 45, 47, 48, 50].includes(note)) {
      const base = { 41: 80, 43: 95, 45: 115, 47: 140, 48: 170, 50: 200 }[note];
      toneHit(t0, base * 1.8, base, 0.05, 0.2, 0.65 * v, (note - 45) / 10, 0.15);
      return;
    }
    if ([49, 52, 55, 57].includes(note)) {
      noiseHit(t0, 'highpass', 4000, 0, 0.7, 0.3 * v, 0.3, 0.2);
      return;
    }
    if ([51, 53, 59].includes(note)) {
      noiseHit(t0, 'bandpass', 8000, 0.8, 0.35, 0.25 * v, 0.35, 0.15);
      [3300, 4700, 6100].forEach((fr) => toneHit(t0, fr, fr, 0, 0.3, 0.07 * v, 0.35));
      return;
    }
    if (note === 54) {
      noiseHit(t0, 'highpass', 6000, 0, 0.07, 0.3 * v, 0.4, 0.1);
      return;
    }
    if (note === 56) {
      toneHit(t0, 560, 560, 0, 0.1, 0.2 * v, -0.3, 0, 'square');
      toneHit(t0, 845, 845, 0, 0.1, 0.2 * v, -0.3, 0, 'square');
      return;
    }
    if (note >= 60 && note <= 66) {
      const f = { 60: 430, 61: 340, 62: 310, 63: 260, 64: 195, 65: 330, 66: 250 }[note];
      const tau = { 60: 0.09, 61: 0.1, 62: 0.035, 63: 0.19, 64: 0.22, 65: 0.12, 66: 0.14 }[note];
      toneHit(t0, f * 1.25, f, 0.02, tau, 0.55 * v, -0.35 + (note - 60) * 0.1, 0.1);
      noiseHit(t0, 'highpass', 2000, 0, 0.004, 0.25 * v, -0.35 + (note - 60) * 0.1);
      return;
    }
    if (note === 67 || note === 68) {
      toneHit(t0, note === 67 ? 1300 : 1000, note === 67 ? 1300 : 1000, 0, 0.1, 0.3 * v, 0.3);
      return;
    }
    if ([69, 70, 82].includes(note)) {
      noiseHit(t0, 'highpass', 8000, 0, 0.045, 0.3 * v, 0.45);
      return;
    }
    if ([75, 76, 77].includes(note)) {
      const f = { 75: 2500, 76: 1400, 77: 1000 }[note];
      toneHit(t0, f, f, 0, 0.025, 0.4 * v, -0.4);
      return;
    }
    if (note === 80 || note === 81) {
      [4200, 6900, 9800].forEach((fr) => toneHit(t0, fr, fr, 0, note === 80 ? 0.04 : 0.4, 0.08 * v, 0.4));
      return;
    }
    noiseHit(t0, 'bandpass', 3000, 0.5, 0.06, 0.3 * v, 0);
  }

  function sidechain(t0) {
    const release = Math.min(0.3, (60 / tempo) * 0.8);
    pump.gain.setValueAtTime(1 - pumpDepth, t0);
    pump.gain.linearRampToValueAtTime(1, t0 + release);
  }

  let tempo = 120;

  return {
    ctx,
    kit,
    setTempo(bpm) { tempo = bpm; setTempo(bpm); },
    setPack(p) { pack = p; },
    note(patch, midi, vel, t0, gate, m = null) {
      let v = Math.min(vel, 1) ** 1.3;
      if (m && m.hard) v = Math.min(1, v * 1.25);
      mods = m && Object.values(m).some(Boolean) ? m : null;
      try {
        if (sampleNote(patch, midi, v, t0, Math.max(gate, 0.02))) return;
        const fn = patches[patch] || patches.pluck;
        fn(midiHz(midi), v, t0, Math.max(gate, 0.02));
      } finally {
        mods = null;
      }
    },
    drum(note, vel, t0) {
      const v = Math.min(vel, 1);
      if (sampleDrum(note, v, t0)) return;
      drum(note, v, t0);
    },
    pumpOn,
    beat(t0) { if (pumpOn && style === 'futurebass') sidechain(t0); },   // beat-grid pump: future bass only
    stopAll() {
      const t = now();
      for (const s of sources) {
        try { s.stop(t); } catch (e) { /* already stopped */ }
      }
      sources.clear();
      pump.gain.cancelScheduledValues(t);
      pump.gain.setValueAtTime(1, t);
    },
    dispose() {
      this.stopAll();
      out.disconnect();
    },
  };
}

const curves = {};
function driveCurve(amount) {
  if (!curves[amount]) {
    const n = 1024;
    const c = new Float32Array(n);
    const norm = Math.tanh(amount);
    for (let i = 0; i < n; i++) c[i] = Math.tanh(((i / (n - 1)) * 2 - 1) * amount) / norm;
    curves[amount] = c;
  }
  return curves[amount];
}

// Convolution reverb from a generated impulse: decaying noise, darker over time
function convolver(ctx, rt60, predelay, damp) {
  const sr = ctx.sampleRate;
  const n = Math.floor((rt60 + predelay) * sr);
  const buf = ctx.createBuffer(2, n, sr);
  const pre = Math.floor(predelay * sr);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    let lp = 0;
    for (let i = pre; i < n; i++) {
      const t = (i - pre) / sr;
      const k = damp + (1 - damp) * Math.min(t / rt60, 1);     // one-pole gets darker with time
      lp += (Math.random() * 2 - 1 - lp) * (1 - k);
      d[i] = lp * Math.exp((-6.91 * t) / rt60);
    }
  }
  let e = 0;
  const d0 = buf.getChannelData(0);
  for (let i = 0; i < n; i++) e += d0[i] * d0[i];
  const norm = 1 / Math.sqrt(e || 1);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    for (let i = 0; i < n; i++) d[i] *= norm * 3;
  }
  const cv = ctx.createConvolver();
  cv.buffer = buf;
  return cv;
}
